import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { installationApi, installerApi, purchaseOrderApi, productApi } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

export default function CreateInstallation() {
  const navigate = useNavigate();
  const [installers, setInstallers] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_address: '',
    installation_date: null,
    installer_id: '',
    po_id: '',
    notes: '',
  });
  
  const [materials, setMaterials] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [installersRes, posRes, productsRes] = await Promise.all([
          installerApi.getAll(),
          purchaseOrderApi.getAll('delivered'),
          productApi.getAll()
        ]);
        setInstallers(installersRes.data);
        setPurchaseOrders(posRes.data);
        setProducts(productsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // Data fetch runs once on mount
  }, []);

  const selectedInstaller = installers.find(i => i.id === formData.installer_id);
  const selectedPO = purchaseOrders.find(po => po.id === formData.po_id);

  const handleAddMaterial = () => {
    setMaterials([...materials, { id: crypto.randomUUID(), product_id: '', quantity: 1 }]);
  };

  const handleRemoveMaterial = (materialId) => {
    setMaterials(materials.filter(m => m.id !== materialId));
  };

  const handleMaterialChange = (materialId, field, value) => {
    setMaterials(materials.map(m => 
      m.id === materialId ? { ...m, [field]: value } : m
    ));
  };

  const getProduct = (productId) => products.find(p => p.id === productId);

  // Auto-populate materials from PO
  useEffect(() => {
    if (selectedPO && materials.length === 0) {
      const poMaterials = selectedPO.items.map(item => ({
        id: crypto.randomUUID(),
        product_id: item.product_id,
        quantity: item.quantity
      }));
      setMaterials(poMaterials);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPO]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.customer_address) {
      toast.error('Please fill in customer details');
      return;
    }
    
    if (!formData.installation_date) {
      toast.error('Please select installation date');
      return;
    }
    
    if (!formData.installer_id) {
      toast.error('Please select an installer');
      return;
    }

    setSubmitting(true);
    
    const installationData = {
      customer_name: formData.customer_name,
      customer_address: formData.customer_address,
      installation_date: format(formData.installation_date, 'yyyy-MM-dd'),
      installer_id: formData.installer_id,
      installer_name: selectedInstaller.name,
      po_id: formData.po_id || null,
      materials: materials.filter(m => m.product_id).map(m => {
        const product = getProduct(m.product_id);
        return {
          product_id: m.product_id,
          product_name: product?.name || 'Unknown',
          quantity: parseInt(m.quantity) || 1
        };
      }),
      notes: formData.notes || null,
    };

    try {
      await installationApi.create(installationData);
      toast.success('Installation scheduled successfully');
      navigate('/installations');
    } catch (error) {
      console.error('Error creating installation:', error);
      toast.error('Failed to schedule installation');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="create-installation-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-sm">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold font-heading">Schedule Installation</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Create a new installation job
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Details */}
            <Card className="border border-border shadow-none rounded-sm">
              <CardHeader className="border-b border-border/50 pb-4">
                <CardTitle className="text-base font-heading">Customer Details</CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                <div>
                  <Label htmlFor="customer_name">Customer Name *</Label>
                  <Input
                    id="customer_name"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    className="rounded-sm mt-1.5"
                    required
                    data-testid="input-customer-name"
                  />
                </div>
                <div>
                  <Label htmlFor="customer_address">Installation Address *</Label>
                  <Textarea
                    id="customer_address"
                    value={formData.customer_address}
                    onChange={(e) => setFormData({ ...formData, customer_address: e.target.value })}
                    className="rounded-sm mt-1.5"
                    rows={2}
                    required
                    data-testid="input-customer-address"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Materials from PO */}
            <Card className="border border-border shadow-none rounded-sm">
              <CardHeader className="border-b border-border/50 pb-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <CardTitle className="text-base font-heading">Materials</CardTitle>
                  <div className="flex gap-2">
                    <Select 
                      value={formData.po_id || "none"} 
                      onValueChange={(value) => setFormData({ ...formData, po_id: value === "none" ? "" : value })}
                    >
                      <SelectTrigger className="w-full sm:w-[200px] rounded-sm" data-testid="select-po">
                        <SelectValue placeholder="Link to PO (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {purchaseOrders.map(po => (
                          <SelectItem key={po.id} value={po.id}>
                            {po.po_number} - {po.vendor_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      onClick={handleAddMaterial}
                      className="rounded-sm"
                      data-testid="btn-add-material"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                {materials.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">
                    No materials added. Link a PO or add materials manually.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {materials.map((material, index) => {
                      const product = getProduct(material.product_id);
                      return (
                        <div key={material.id} className="flex gap-4 items-start p-4 bg-muted/30 rounded-sm" data-testid={`material-${index}`}>
                          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <Label className="text-xs">Product</Label>
                              <Select 
                                value={material.product_id}
                                onValueChange={(value) => handleMaterialChange(material.id, 'product_id', value)}
                              >
                                <SelectTrigger className="mt-1 rounded-sm" data-testid={`select-material-${index}`}>
                                  <SelectValue placeholder="Select product" />
                                </SelectTrigger>
                                <SelectContent>
                                  {products.map(p => (
                                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs">Quantity</Label>
                              <Input
                                type="number"
                                min="1"
                                value={material.quantity}
                                onChange={(e) => handleMaterialChange(material.id, 'quantity', parseInt(e.target.value) || 1)}
                                className="mt-1 rounded-sm font-mono-data"
                                data-testid={`input-material-qty-${index}`}
                              />
                            </div>
                          </div>
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleRemoveMaterial(material.id)}
                            className="text-destructive mt-5"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Notes */}
            <Card className="border border-border shadow-none rounded-sm">
              <CardHeader className="border-b border-border/50 pb-4">
                <CardTitle className="text-base font-heading">Notes</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Add any notes or special instructions..."
                  className="rounded-sm"
                  rows={3}
                  data-testid="input-installation-notes"
                />
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Schedule */}
            <Card className="border border-border shadow-none rounded-sm">
              <CardHeader className="border-b border-border/50 pb-4">
                <CardTitle className="text-base font-heading">Schedule</CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                <div>
                  <Label className="text-xs">Installation Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full mt-1 rounded-sm justify-start text-left font-normal",
                          !formData.installation_date && "text-muted-foreground"
                        )}
                        data-testid="btn-installation-date"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.installation_date ? format(formData.installation_date, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.installation_date}
                        onSelect={(date) => setFormData({ ...formData, installation_date: date })}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <Label className="text-xs">Assigned Installer *</Label>
                  <Select 
                    value={formData.installer_id} 
                    onValueChange={(value) => setFormData({ ...formData, installer_id: value })}
                  >
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="select-installer">
                      <SelectValue placeholder="Select installer" />
                    </SelectTrigger>
                    <SelectContent>
                      {installers.map(installer => (
                        <SelectItem key={installer.id} value={installer.id}>
                          {installer.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <Button 
              type="submit"
              className="w-full rounded-sm"
              disabled={submitting}
              data-testid="btn-schedule-installation"
            >
              {submitting ? 'Scheduling...' : 'Schedule Installation'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
